import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Tooltip,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ThermostatIcon from '@mui/icons-material/Thermostat';

/**
 * SessionStressHeatmap Component
 * Displays a heatmap showing stress levels for a specific session's duration
 * Shows actual hourly breakdown based on the session start time and pie_data
 * pie_data: array where each element represents 1 second (1=green/no stress, 2=yellow/intensive, 3=red/dangerous)
 */
function SessionStressHeatmap({ pieData, startTime }) {
  const theme = useTheme();
  const { t } = useTranslation();

  // Parse pie_data to create hourly breakdown
  const getHourlyBreakdown = () => {
    if (!pieData || !pieData.length || !startTime) {
      return [];
    }

    const sessionStart = new Date(startTime);
    
    // Initialize all 24 hours with zero data
    const hourlyData = {};
    for (let h = 0; h < 24; h++) {
      hourlyData[h] = {
        hour: h,
        no_stress: 0,      // pie_data value = 1
        intensive: 0,      // pie_data value = 2
        dangerous: 0,      // pie_data value = 3
        total: 0,
      };
    }
    
    // Process each second of pie_data
    pieData.forEach((value, index) => {
      // Calculate the time for this data point (session start + seconds elapsed)
      const currentTime = new Date(sessionStart.getTime() + index * 1000);
      const hour = currentTime.getHours();
      
      hourlyData[hour].total += 1;
      
      // pie_data values: 1 = green (no stress), 2 = yellow (intensive), 3 = red (dangerous)
      if (value === 3) {
        hourlyData[hour].dangerous += 1;
      } else if (value === 2) {
        hourlyData[hour].intensive += 1;
      } else if (value === 1) {
        hourlyData[hour].no_stress += 1;
      }
    });

    // Convert to array and calculate stress levels for all 24 hours
    // Stress level calculation based on actual thresholds:
    // - No stress (value=1): 0-5% range → contributes 2.5% average
    // - Intensive (value=2): 5-52.75% range → contributes 28.875% average
    // - Dangerous (value=3): 52.75-100% range → contributes 76.375% average
    return Array.from({ length: 24 }, (_, hour) => {
      const data = hourlyData[hour];
      let stressLevel = 0;
      
      if (data.total > 0) {
        // Use midpoint of each range for weighted average
        const noStressContribution = (data.no_stress / data.total) * 2.5;        // 0-5% midpoint
        const intensiveContribution = (data.intensive / data.total) * 28.875;    // 5-52.75% midpoint
        const dangerousContribution = (data.dangerous / data.total) * 76.375;    // 52.75-100% midpoint
        
        stressLevel = noStressContribution + intensiveContribution + dangerousContribution;
      }
      
      return {
        ...data,
        stress_level: Math.round(stressLevel * 100) / 100,
      };
    });
  };

  const hourlyBreakdown = getHourlyBreakdown();

  // Get color based on stress level category (matching line chart colors)
  const getStressColor = (stressLevel) => {
    if (stressLevel === 0) {
      return theme.palette.mode === 'dark' ? '#2a2a2a' : '#e0e0e0';
    }
    if (stressLevel <= 5) {
      return '#4caf50'; // Green (balanced)
    } else if (stressLevel <= 52.75) {
      return '#fbc02d'; // Yellow (intensive)
    } else {
      return '#f44336'; // Red (dangerous)
    }
  };

  // Calculate percentage through the current range (for the indicator bar)
  const getRangeProgress = (stressLevel) => {
    if (stressLevel === 0) return 0;
    
    if (stressLevel <= 5) {
      // 0-5%: Green range
      return (stressLevel / 5) * 100;
    } else if (stressLevel <= 52.75) {
      // 5-52.75%: Yellow range
      return ((stressLevel - 5) / 47.75) * 100;
    } else {
      // 52.75-100%: Red range
      return ((stressLevel - 52.75) / 47.25) * 100;
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getStressLabel = (level) => {
    if (level === 0) return t('heatmap.no_data', 'No Data');
    if (level <= 5) return t('heatmap.low_stress', 'Low Stress');
    if (level <= 52.75) return t('heatmap.moderate_stress', 'Moderate Stress');
    return t('heatmap.high_stress', 'High Stress');
  };

  if (hourlyBreakdown.length === 0) {
    return null;
  }

  const maxStress = Math.max(...hourlyBreakdown.map(h => h.stress_level), 1);
  const peakHours = hourlyBreakdown
    .filter(h => h.stress_level > 0 && h.total > 0)
    .sort((a, b) => b.stress_level - a.stress_level)
    .slice(0, 3);
  
  // Find the range of hours that have data
  const hoursWithData = hourlyBreakdown.filter(h => h.total > 0);
  const hasData = hoursWithData.length > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <ThermostatIcon color="primary" />
          <Typography variant="h6">
            {t('heatmap.session_title', 'Session Stress Distribution - Hourly')}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('heatmap.session_description', 'Showing stress levels for each hour during this session')}
        </Typography>

        {peakHours.length > 0 && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('heatmap.peak_hours', 'Peak Stress Hours')}:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {peakHours.map((peak) => (
                <Chip
                  key={peak.hour}
                  label={`${peak.hour.toString().padStart(2, '0')}:00 (${peak.stress_level.toFixed(0)}%)`}
                  size="small"
                  sx={{
                    bgcolor: `${getStressColor(peak.stress_level)}40`,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Heatmap visualization */}
        <Box sx={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
          <Stack spacing={0.5}>
            {/* Hour labels */}
            <Stack direction="row" spacing={0.5} mb={1}>
              {hourlyBreakdown.map((hourData) => (
                <Box
                  key={hourData.hour}
                  sx={{
                    minWidth: '32px',
                    flex: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {hourData.hour.toString().padStart(2, '0')}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Heatmap bars */}
            <Stack direction="row" spacing={0.5}>
              {hourlyBreakdown.map((hourData) => (
                <Tooltip
                  key={hourData.hour}
                  title={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {hourData.hour.toString().padStart(2, '0')}:00 - {(hourData.hour + 1).toString().padStart(2, '0')}:00
                      </Typography>
                      <Typography variant="caption" display="block">
                        {getStressLabel(hourData.stress_level)}
                      </Typography>
                      {hourData.total > 0 && (
                        <>
                          <Typography variant="caption" display="block">
                            {t('heatmap.stress_level', 'Stress Level')}: {hourData.stress_level.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('heatmap.total_time', 'Total Time')}: {formatTime(hourData.total)}
                          </Typography>
                          <Typography variant="caption" display="block" mt={0.5}>
                            {t('heatmap.balanced', 'Balanced')}: {formatTime(hourData.no_stress)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('heatmap.intensive', 'Intensive')}: {formatTime(hourData.intensive)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('heatmap.dangerous', 'Dangerous')}: {formatTime(hourData.dangerous)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box
                    sx={{
                      minWidth: '32px',
                      flex: 1,
                      height: '120px',
                      bgcolor: getStressColor(hourData.stress_level),
                      borderRadius: 1,
                      cursor: hourData.total > 0 ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': hourData.total > 0 ? {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4],
                      } : {},
                    }}
                  >
                    {/* Range progress indicator - shows how far through the current range (0-5%, 5-52.75%, or 52.75-100%) */}
                    {hourData.stress_level > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: `${getRangeProgress(hourData.stress_level)}%`,
                          bgcolor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Stack>

            {/* Legend */}
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {t('heatmap.legend', 'Legend')}:
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', borderRadius: 0.5 }} />
                  <Typography variant="caption">{t('heatmap.low', 'Low (0-5%)')}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#fbc02d', borderRadius: 0.5 }} />
                  <Typography variant="caption">{t('heatmap.moderate', 'Moderate (5-52.75%)')}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#f44336', borderRadius: 0.5 }} />
                  <Typography variant="caption">{t('heatmap.high', 'High (52.75-100%)')}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default SessionStressHeatmap;
